import { AppBar } from "@/elements/components/ui/app-bar";
import { Tab } from "@/elements/components/ui/tab";
import { api } from "@/elements/components/utils/convex-api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Select } from "@/elements/components/ui/select";
import { Tag } from "@/elements/components/ui/tag";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { Button } from "@/elements/components/ui/button";
import { Id } from "@teachloop/backend/_generated/dataModel";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { TeachingExperienceFormData } from "./teaching-experience";
import goBack from "@/elements/components/utils/go-back";
import { buildCurriculumYearGroupTabs } from "@/elements/components/sheets/multi-select-sheet";
import {
  isBritishCurriculumLabel,
  isIbCurriculumLabel,
} from "@/elements/components/utils/curriculum";

const safeParse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(error);
    return null;
  }
};

const SubjectYearGroupsSchema = z.object({
  subjectYearGroups: z.array(
    z.object({
      subjectId: z.string().min(1),
      yearGroupIds: z.array(z.string().min(1)).min(1),
    }),
  ),
});

type SubjectYearGroupsSchemaType = z.infer<typeof SubjectYearGroupsSchema>;

type OnboardingPayload = TeachingExperienceFormData & {
  curriculumIds: Id<"curriculum">[];
  programmeIds?: Id<"keyStages">[];
  subjectYearGroups: {
    subjectId: Id<"subjects">;
    yearGroupIds: Id<"yearGroups">[];
  }[];
};

function YearGroupsScreen() {
  const router = useRouter();
  const { teachingExperienceWithCurriculumsAndSubjects } =
    useLocalSearchParams();
  const teachingExperienceData = safeParse(
    teachingExperienceWithCurriculumsAndSubjects as string,
  ) as OnboardingPayload;
  const subjectYearGroups = teachingExperienceData?.subjectYearGroups ?? [];
  const curriculumIds = teachingExperienceData?.curriculumIds ?? [];
  const programmeIds = teachingExperienceData?.programmeIds ?? [];

  const {
    control,
    watch,
    setValue,
    formState: { isValid: isFormValid },
    handleSubmit,
  } = useForm<SubjectYearGroupsSchemaType>({
    resolver: zodResolver(SubjectYearGroupsSchema),
    mode: "onChange",
    defaultValues: {
      subjectYearGroups: subjectYearGroups,
    },
  });
  const [activeSubjectId, setActiveSubjectId] = useState(
    watch("subjectYearGroups")?.[0]?.subjectId,
  );
  const activeSubjectIndex = watch("subjectYearGroups").findIndex(
    (subject) => subject.subjectId === activeSubjectId,
  );
  const { open: openMultiSelectSheet } = useSheet<any, any>(
    SheetIds.MULTI_SELECT,
  );

  const { data: subjectsList = [] } = useQuery({
    ...convexQuery(api.tutor.onboarding.queries.getAllSubjects, {}),
    enabled: !!teachingExperienceData,
  });

  const { data: curriculums = [] } = useQuery({
    ...convexQuery(api.tutor.onboarding.queries.getAvailableCurriculums, {}),
    enabled: !!teachingExperienceData,
  });

  const ibCurriculumId = useMemo(
    () =>
      curriculums.find((curriculum) =>
        isIbCurriculumLabel(curriculum.label),
      )?.value,
    [curriculums],
  );

  const hasIbCurriculum = useMemo(
    () =>
      !!ibCurriculumId &&
      curriculumIds.some((curriculumId) => curriculumId === ibCurriculumId),
    [curriculumIds, ibCurriculumId],
  );

  const hasBritishCurriculum = useMemo(
    () =>
      curriculumIds.some((curriculumId) =>
        isBritishCurriculumLabel(
          curriculums.find((curriculum) => curriculum.value === curriculumId)
            ?.label,
        ),
      ),
    [curriculumIds, curriculums],
  );

  const isIbOnly = hasIbCurriculum && !hasBritishCurriculum;
  const yearGroupNoun = isIbOnly ? "grades" : "year groups";
  const yearGroupNounTitle = isIbOnly ? "grades" : "year groups";
  const selectYearGroupTitle = isIbOnly
    ? "Select grades"
    : "Select year groups";

  const { data: programmes = [] } = useQuery(
    convexQuery(
      api.tutor.onboarding.queries.getProgrammesForCurriculum,
      hasIbCurriculum && ibCurriculumId
        ? { curriculumId: ibCurriculumId as Id<"curriculum"> }
        : "skip",
    ),
  );

  const selectedProgrammes = useMemo(
    () =>
      programmes.filter((programme) => programmeIds.includes(programme.value)),
    [programmes, programmeIds],
  );

  const { data: yearGroupsForSubject = [] } = useQuery(
    convexQuery(
      api.tutor.onboarding.queries.getYearGroupsListBySubjectId,
      activeSubjectId
        ? {
            subjectId: activeSubjectId as Id<"subjects">,
            curriculumIds,
            programmeIds:
              programmeIds.length > 0 ? programmeIds : undefined,
          }
        : "skip",
    ),
  );

  const { mutateAsync: createTutorProfile } = useMutation({
    mutationFn: useConvexMutation(api.tutor.onboarding.mutations.createProfile),
  });

  const subjectsToDisplay: typeof subjectsList = subjectsList.filter(
    (subject) =>
      subjectYearGroups.some(
        (subjectData: { subjectId: string }) =>
          subjectData.subjectId === subject.value,
      ),
  );

  const handleYearGroupOptionsOpen = useCallback(async () => {
    const currentYearGroupIds =
      watch("subjectYearGroups")?.[activeSubjectIndex]?.yearGroupIds ?? [];
    const tabs = buildCurriculumYearGroupTabs({
      yearGroups: yearGroupsForSubject,
      curriculums,
      selectedCurriculumIds: curriculumIds,
    });
    const result = await openMultiSelectSheet({
      options: yearGroupsForSubject,
      preSelectedOptions: currentYearGroupIds,
      title: selectYearGroupTitle,
      tabs,
    });
    if (
      result?.selectedOptions &&
      JSON.stringify(result.selectedOptions) !==
        JSON.stringify(currentYearGroupIds)
    ) {
      setValue(
        `subjectYearGroups.${activeSubjectIndex}.yearGroupIds`,
        result.selectedOptions,
        {
          shouldValidate: true,
        },
      );
    }
  }, [
    openMultiSelectSheet,
    yearGroupsForSubject,
    curriculums,
    curriculumIds,
    selectYearGroupTitle,
    watch,
    setValue,
    activeSubjectIndex,
  ]);

  const handleNext = useCallback(
    async (data: SubjectYearGroupsSchemaType) => {
      const timezoneIdentifier =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      await createTutorProfile({
        subjectYearGroups: data.subjectYearGroups.map((subject) => ({
          subjectId: subject.subjectId as Id<"subjects">,
          yearGroupIds: subject.yearGroupIds as Id<"yearGroups">[],
        })),
        onboardingStepCompleted: "details",
        countryOfResidenceId:
          teachingExperienceData.countryOfResidenceId as Id<"countriesOfResidences">,
        teachingQualificationCountryOfIssue:
          teachingExperienceData.teachingQualificationCountryOfIssue,
        detailsOfTeachingQualification:
          teachingExperienceData.detailsOfTeachingQualification,
        detailsOfEducationalDegree:
          teachingExperienceData.detailsOfEducationalDegree,
        teachingExperience: teachingExperienceData.teachingExperience,
        examBoardMarker: teachingExperienceData.examBoardMarker,
        currentWorkplace: teachingExperienceData.currentWorkplace,
        additionalInfo: teachingExperienceData.additionalInfo ?? "",
        languageIds: teachingExperienceData.languageIds as Id<"languages">[],
        hasTeachingQualification:
          teachingExperienceData.hasTeachingQualification,
        curriculumIds:
          teachingExperienceData.curriculumIds as Id<"curriculum">[],
        detailsOfExamBoard: teachingExperienceData.detailsOfExamBoard,
        timezoneIdentifier,
        phone: teachingExperienceData.phone,
      });
      router.replace("/(app)/onboarding/(tutor)/tutor-legal");
    },
    [createTutorProfile, teachingExperienceData, router],
  );

  if (!teachingExperienceData) {
    return <Redirect href="/(app)/onboarding/(tutor)/teaching-experience" />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Which {yearGroupNounTitle} can you teach?
              </Text>
              <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                Pick relevant {yearGroupNoun} for each subject.
              </Text>
            </View>
            <View className="h-8" />
            {selectedProgrammes.length > 0 ? (
              <View className="gap-4 mb-8">
                <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
                  Selected IB programmes
                </Text>
                <ScrollView
                  horizontal
                  contentContainerClassName="gap-4"
                  showsHorizontalScrollIndicator={false}
                >
                  {selectedProgrammes.map((programme) => (
                    <Tag size="md" key={programme.value}>
                      <Tag.Text size="md">{programme.label}</Tag.Text>
                    </Tag>
                  ))}
                </ScrollView>
              </View>
            ) : null}
            <ScrollView
              horizontal
              contentContainerClassName="gap-2"
              showsHorizontalScrollIndicator={false}
            >
              {subjectsToDisplay.map((subject) => (
                <Tab
                  key={subject.value}
                  intent={
                    subject.value === activeSubjectId ? "active" : "inActive"
                  }
                  onPress={() => {
                    setActiveSubjectId(subject?.value);
                  }}
                  size="md"
                >
                  <Tab.Text size="md">{subject?.label}</Tab.Text>
                </Tab>
              ))}
            </ScrollView>
            <View className="h-8" />
            <View className="gap-6" key={activeSubjectId}>
              <View className="gap-4">
                <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
                  Choose relevant {yearGroupNoun}
                </Text>
                <Controller
                  control={control}
                  name={`subjectYearGroups.${activeSubjectIndex}.yearGroupIds`}
                  render={({ field }) => (
                    <Select size="md" onPress={handleYearGroupOptionsOpen}>
                      <Select.Label size="md">
                        {field.value.length > 0
                          ? `Selected ${yearGroupNoun}`
                          : selectYearGroupTitle}
                      </Select.Label>
                      <Select.DownArrow />
                    </Select>
                  )}
                />
                <Select.Results
                  selectedOptions={yearGroupsForSubject.filter((option) =>
                    watch(
                      `subjectYearGroups.${activeSubjectIndex}.yearGroupIds`,
                    ).includes(option.value),
                  )}
                  onRemove={(value) => {
                    setValue(
                      `subjectYearGroups.${activeSubjectIndex}.yearGroupIds`,
                      watch(
                        `subjectYearGroups.${activeSubjectIndex}.yearGroupIds`,
                      ).filter((option) => option !== value),
                      {
                        shouldValidate: true,
                      },
                    );
                  }}
                />
              </View>
            </View>
            <View className="h-8" />
            <Button
              disabled={!isFormValid}
              fullWidth
              size="md"
              onPress={handleSubmit(handleNext)}
            >
              <Button.Text size="md">Done</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default YearGroupsScreen;
